// ============================================================================
// client/maillet-papier.ts — LES MAILLETS ET LES PRISES (06/08)
// ----------------------------------------------------------------------------
// Audit demandé par Claude-Alain : « c'est pas encore cela. Il faut un audit qui
// débouche sur plusieurs visuels du maillet, plusieurs façons de le tenir et
// plusieurs façons de frapper. »
//
// CE QUI N'ALLAIT PAS dans la première version, constaté à la pellicule :
//   · un seul dessin, dérivé de l'objet au sol — donc pensé pour être VU DE
//     FACE dans une case, pas TENU de profil par un bonhomme de 44 px ;
//   · la tête ne faisait que 26 px de large : à l'écran, un maillet qui doit
//     expédier quelqu'un à trois cases doit avoir l'air LOURD ;
//   · une seule prise (épaule) et un seul arc, donc rien à quoi comparer.
//
// D'où ce fichier : QUATRE maillets × TROIS prises, tous dessinés dans le même
// langage papier découpé, tous peints par la planche ET par le jeu — une
// maquette qui mentirait sur le rendu final ne servirait à rien.
// Le jeu peint `MAILLET_CHOISI` / `PRISE_CHOISIE` : deux nombres à changer.
// ============================================================================

import { decoupe, rgba } from './papier'

type Ctx = CanvasRenderingContext2D

export type MailletModele = 0 | 1 | 2 | 3
export type MailletPrise = 0 | 1 | 2

export const MAILLET_NOMS = ['La masse de forgeron', 'Le maillet de foire', 'Le marteau-enclume', 'Le maillet à ressort'] as const
export const PRISE_NOMS = ['Sur l\'épaule', 'À deux mains devant', 'Traîné au sol'] as const

/** Ce que le JEU peint. Deux nombres, rien d'autre à toucher. */
export const MAILLET_CHOISI: MailletModele = 0
export const PRISE_CHOISIE: MailletPrise = 0

const BOIS = '#a06a34', BOIS_SOMBRE = '#7a4f26'
const FER = '#9aa8b4', FER_CLAIR = '#c8d2da', FER_SOMBRE = '#6b7783'
const OR = '#e8a020'
const RUBAN = '#c0392b'

/**
 * Un maillet dessiné DEBOUT, manche vers le bas, tête en haut, centré sur (0,0)
 * au bout du manche (le point de POIGNE). `S` = longueur totale visée.
 * C'est cette convention qui permet aux prises de le faire pivoter sans calcul.
 */
export function mailletPapier(g: Ctx, S: number, modele: MailletModele = MAILLET_CHOISI): void {
  const k = S / 48
  const boite = { x: -S * 2, y: -S * 2, w: S * 4, h: S * 4 }
  if (modele === 0) {
    // LA MASSE DE FORGERON : une tête d'acier massive, deux joues cerclées, un
    // manche épais. La plus lourde à l'œil : on croit au bump de trois cases.
    g.fillStyle = BOIS
    g.fillRect(-3 * k, -34 * k, 6 * k, 34 * k)
    g.fillStyle = rgba(BOIS_SOMBRE, 0.6); g.fillRect(-3 * k, -12 * k, 6 * k, 3 * k) // la virole
    decoupe(g, () => { g.beginPath(); g.rect(-19 * k, -46 * k, 38 * k, 15 * k) }, FER, 'quadrille', boite)
    g.fillStyle = rgba(FER_SOMBRE, 0.85)
    g.fillRect(-19 * k, -46 * k, 5 * k, 15 * k); g.fillRect(14 * k, -46 * k, 5 * k, 15 * k)
    g.fillStyle = rgba(FER_CLAIR, 0.75); g.fillRect(-13 * k, -44 * k, 26 * k, 3 * k)  // le reflet
  } else if (modele === 1) {
    // LE MAILLET DE FOIRE : le gros maillet de bois rouge et crème des fêtes
    // foraines, tête bombée. Le plus « jeu de famille », le moins violent.
    g.fillStyle = BOIS
    g.fillRect(-3 * k, -32 * k, 6 * k, 32 * k)
    decoupe(g, () => { g.beginPath(); g.ellipse(0, -40 * k, 21 * k, 11 * k, 0, 0, 7) }, '#e8ddc4', 'plis', boite)
    g.fillStyle = rgba(RUBAN, 0.9)
    g.beginPath(); g.ellipse(-12 * k, -40 * k, 7 * k, 10 * k, 0, 0, 7); g.fill()
    g.beginPath(); g.ellipse(12 * k, -40 * k, 7 * k, 10 * k, 0, 0, 7); g.fill()
    g.fillStyle = rgba('#ffffff', 0.5)
    g.beginPath(); g.ellipse(-3 * k, -44 * k, 5 * k, 3 * k, -0.3, 0, 7); g.fill()
  } else if (modele === 2) {
    // LE MARTEAU-ENCLUME : tête carrée d'un côté, panne fendue de l'autre — la
    // silhouette la plus LISIBLE de profil, celle qui dit « outil » d'un coup d'œil.
    g.fillStyle = BOIS
    g.fillRect(-3 * k, -33 * k, 6 * k, 33 * k)
    decoupe(g, () => {
      g.beginPath()
      g.moveTo(-20 * k, -45 * k); g.lineTo(6 * k, -45 * k); g.lineTo(20 * k, -39 * k)
      g.lineTo(20 * k, -35 * k); g.lineTo(6 * k, -31 * k); g.lineTo(-20 * k, -31 * k)
      g.closePath()
    }, FER, 'quadrille', boite)
    g.fillStyle = rgba(FER_SOMBRE, 0.8); g.fillRect(-20 * k, -45 * k, 6 * k, 14 * k)
    g.fillStyle = rgba(FER_CLAIR, 0.7); g.fillRect(-13 * k, -43 * k, 16 * k, 2.5 * k)
    g.fillStyle = rgba(OR, 0.85); g.fillRect(-3.5 * k, -31 * k, 7 * k, 3 * k) // le coin doré
  } else {
    // LE MAILLET À RESSORT : la première version, gardée comme témoin — c'est
    // celle qu'il a jugée « pas encore cela », elle sert de point de comparaison.
    g.fillStyle = BOIS; g.fillRect(-2.5 * k, -30 * k, 5 * k, 30 * k)
    g.strokeStyle = FER; g.lineWidth = 3.5 * k; g.lineCap = 'round'
    for (let i = 0; i < 3; i++) { g.beginPath(); g.ellipse(0, -32 * k - i * 4 * k, (8 - i) * k, 2.6 * k, 0, 0, 7); g.stroke() }
    decoupe(g, () => { g.beginPath(); g.rect(-14 * k, -55 * k, 28 * k, 11 * k) }, '#c8863a', 'grain', boite)
    g.fillStyle = rgba('#8a5a24', 0.9); g.fillRect(-14 * k, -55 * k, 4 * k, 11 * k); g.fillRect(10 * k, -55 * k, 4 * k, 11 * k)
  }
}

/** L'angle et le point de POIGNE d'une prise, au REPOS. Le rendu applique la
 *  translation puis la rotation ; l'arc de frappe repart de `ang`. */
export interface PriseRepos { dx: number; dy: number; ang: number; taille: number }

export function priseRepos(p: MailletPrise, TS: number): PriseRepos {
  if (p === 1) {
    // À DEUX MAINS DEVANT : le manche à l'horizontale devant le torse, tête en
    // avant. La posture de garde — on VOIT qu'il est armé, même immobile.
    return { dx: TS * 0.30, dy: TS * 0.55, ang: -1.75, taille: TS * 0.62 }
  }
  if (p === 2) {
    // TRAÎNÉ AU SOL : le manche pend, la tête racle le sol derrière lui. Le plus
    // lourd, le plus menaçant, et il dégage complètement la silhouette du héros.
    return { dx: -TS * 0.22, dy: TS * 0.86, ang: -2.75, taille: TS * 0.58 }
  }
  // SUR L'ÉPAULE : la prise classique, tête en haut derrière la tête du héros.
  return { dx: TS * 0.34, dy: TS * 0.68, ang: -0.95, taille: TS * 0.55 }
}
